import { useEffect } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { AppSidebar } from "@/components/shell/app-sidebar";
import { SidebarMobileCloseOnRouteChange } from "@/components/shell/sidebar-mobile-close-on-route-change";
import { AppHeader } from "@/components/shell/app-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { tailwind } from "@/utilities/tailwind-utils";
import { config } from "@/config/environment";
import { LoadingCard } from "@/components/ui/loading-card";
import { useUserContext } from "@/context/user-context";
import { BreadcrumbOverrideProvider } from "@/context/breadcrumb-context";

export function AppShell() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    user,
    getRedirectRoute,
    isLoading: userDetailsLoading
  } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();

  // Simplified root path handling - single redirect attempt
  useEffect(() => {
    // Only handle root path redirects when user is authenticated and not loading
    if (
      location.pathname === "/" &&
      user &&
      getRedirectRoute &&
      isAuthenticated &&
      !userDetailsLoading
    ) {
      if (config.debug.enabled) {
        console.log("🔄 AppShell: Handling root path redirect", {
          user,
          isAuthenticated,
          currentPath: location.pathname
        });
      }

      // Get the redirect route from the auth hook
      const redirectRoute = getRedirectRoute();

      if (config.debug.enabled) {
        console.log("🔍 AppShell: Redirect route determined:", redirectRoute);
      }

      // If redirectRoute is null, it means user details are still loading - don't redirect yet
      if (redirectRoute === null) {
        if (config.debug.enabled) {
          console.log(
            "⏳ AppShell: User details still loading, waiting for redirect route..."
          );
        }
        return;
      }

      // Only redirect if we have a valid route that's different from current
      if (redirectRoute && redirectRoute !== "/") {
        if (config.debug.enabled) {
          console.log("✅ AppShell: Redirecting to:", redirectRoute);
        }
        navigate(redirectRoute, { replace: true });
      } else {
        if (config.debug.enabled) {
          console.warn(
            "⚠️ AppShell: No valid redirect route found, staying on root"
          );
        }
      }
    }
  }, [
    location.pathname,
    user,
    isAuthenticated,
    getRedirectRoute,
    userDetailsLoading,
    navigate
  ]);

  // Show loading state only when on root path and user is authenticated but no redirect has happened yet
  if (
    location.pathname === "/" &&
    user &&
    isAuthenticated &&
    getRedirectRoute
  ) {
    const redirectRoute = getRedirectRoute();

    if (config.debug.enabled) {
      console.log("⏳ AppShell: Showing loading spinner for root path", {
        redirectRoute,
        user: !!user,
        isAuthenticated
      });
    }

    // If we have a valid redirect route, show loading spinner
    if (redirectRoute && redirectRoute !== "/") {
      return (
        <LoadingCard
          spinnerSize="lg"
          fullScreen={true}
          showThemeToggle={true}
        />
      );
    }

    // If no valid redirect route, show a different message
    return (
      <LoadingCard spinnerSize="lg" fullScreen={true} showThemeToggle={true} />
    );
  }

  // Show loading state while authentication or user details are loading
  if (authLoading || (isAuthenticated && userDetailsLoading)) {
    if (config.debug.enabled) {
      console.log("⏳ AppShell: Showing loading state", {
        authLoading,
        isAuthenticated,
        userDetailsLoading,
        user: !!user
      });
    }
    return (
      <LoadingCard spinnerSize="lg" fullScreen={true} showThemeToggle={true} />
    );
  }

  // If user is not authenticated, render only the outlet without sidebar
  if (!isAuthenticated) {
    return <Outlet />;
  }

  // For authenticated users with loaded user details, render the full sidebar layout
  return (
    <BreadcrumbOverrideProvider>
      <SidebarProvider>
        <SidebarMobileCloseOnRouteChange />
        <AppSidebar variant="inset" />
        <SidebarInset>
          <AppHeader />
          <div className={tailwind.layout.container}>
            <div className={tailwind.layout.containerMain}>
              <div className={tailwind.components.pageContainer}>
                <Outlet />
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </BreadcrumbOverrideProvider>
  );
}
